import { DANCE_LEVELS } from "./dance";

const ROLE_SORT_ORDER = { Leader: 0, Follower: 1, Both: 2 };

export function filterKey(filter) {
  if (!filter) return null;
  if (filter.id === "dance_form") return `dance_form:${filter.name}`;
  if (filter.id === "dance_level") return `dance_level:${filter.name}:${filter.level}`;
  return filter.id;
}

export function toggleFilter(current, next) {
  return filterKey(current) === filterKey(next) ? null : next;
}

export function matchesGuestFilter(rsvp, filter) {
  if (!filter) return true;
  switch (filter.id) {
    case "first_timer":
      return Boolean(rsvp.first_timer);
    case "beginner_level":
      return (rsvp.dance_forms || []).some((df) => df.level === "Beginner");
    case "hide_name":
      return Boolean(rsvp.hide_name);
    case "dance_form":
      return (rsvp.dance_forms || []).some((df) => df.name === filter.name);
    case "dance_level":
      return (rsvp.dance_forms || []).some(
        (df) => df.name === filter.name && df.level === filter.level,
      );
    default:
      return true;
  }
}

export function countMatchingGuests(rsvps, filter) {
  if (!filter) return rsvps.length;
  return rsvps.filter((r) => matchesGuestFilter(r, filter)).length;
}

export function filterGuestsBySearch(rsvps, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return rsvps;
  return rsvps.filter((r) => (r.name || "").toLowerCase().includes(needle));
}

export function sortGuests(rsvps, sortKey) {
  const list = [...rsvps];
  if (sortKey === "role") {
    return list.sort((a, b) => {
      const roleDiff = (ROLE_SORT_ORDER[a.role] ?? 99) - (ROLE_SORT_ORDER[b.role] ?? 99);
      if (roleDiff !== 0) return roleDiff;
      return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    });
  }
  return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
}

export function countBeginnerLevelDancers(rsvps) {
  const dancerIds = new Set();
  for (const rsvp of rsvps) {
    if ((rsvp.dance_forms || []).some((df) => df.level === "Beginner")) {
      dancerIds.add(rsvp.dancer_id);
    }
  }
  return dancerIds.size;
}

export function formatLevelBreakdown(levels) {
  return levels
    .filter(({ count }) => count > 0)
    .map(({ level, count }) => formatLevelCountLabel(count, level))
    .join(", ");
}

export function formatLevelCountLabel(count, level) {
  const label = count === 1 ? level : `${level}s`;
  return `${count} ${label}`;
}

export function formatDanceFormCountLabel(total, name) {
  return `${total} ${name}`;
}

export function hasAnalytics({
  firstTimerCount,
  beginnerLevelCount,
  danceFormStats,
  hiddenNameCount = 0,
}) {
  return (
    firstTimerCount > 0
    || beginnerLevelCount > 0
    || danceFormStats.length > 0
    || hiddenNameCount > 0
  );
}

/** @deprecated use hasAnalytics */
export const hasCompactAnalytics = hasAnalytics;

/** @deprecated use hasAnalytics */
export const hasFullAnalytics = hasAnalytics;

export function buildCompactAnalyticsLine({
  firstTimerCount,
  beginnerLevelCount,
  danceFormStats,
  hiddenNameCount = 0,
}) {
  const segments = [];

  if (firstTimerCount > 0) {
    segments.push({
      id: "first_timer",
      label: `${firstTimerCount} first-timer${firstTimerCount === 1 ? "" : "s"}`,
      filter: { id: "first_timer" },
      testId: "analytics-first-timers",
    });
  }

  if (beginnerLevelCount > 0) {
    segments.push({
      id: "beginner_level",
      label: `${beginnerLevelCount} beginner skill`,
      filter: { id: "beginner_level" },
      testId: "analytics-beginner-level",
    });
  }

  for (const stat of danceFormStats) {
    segments.push({
      id: `dance_form:${stat.name}`,
      label: formatDanceFormCountLabel(stat.total, stat.name),
      filter: { id: "dance_form", name: stat.name },
      testId: `analytics-dance-${stat.name.toLowerCase()}`,
    });
  }

  if (hiddenNameCount > 0) {
    segments.push({
      id: "hide_name",
      label: `${hiddenNameCount} hidden on public list`,
      filter: { id: "hide_name" },
      testId: "analytics-hidden-names",
    });
  }

  return segments;
}

export function buildDanceFormLevelDetail(danceFormStats) {
  return danceFormStats
    .map((stat) => ({
      name: stat.name,
      total: stat.total,
      filter: { id: "dance_form", name: stat.name },
      testId: `analytics-dance-${stat.name.toLowerCase()}`,
      levelParts: stat.levels
        .filter(({ count }) => count > 0)
        .map(({ level, count }) => ({
          level,
          count,
          label: formatLevelCountLabel(count, level),
          filter: { id: "dance_level", name: stat.name, level },
          testId: `analytics-dance-${stat.name.toLowerCase()}-${level.toLowerCase()}`,
        })),
    }))
    .filter((form) => form.levelParts.length > 0);
}

export function buildDanceFormStats(rsvps) {
  const forms = new Map();

  for (const rsvp of rsvps) {
    for (const df of rsvp.dance_forms || []) {
      if (!df?.name) continue;
      if (!forms.has(df.name)) {
        forms.set(df.name, {
          dancerIds: new Set(),
          levels: Object.fromEntries(DANCE_LEVELS.map((level) => [level, new Set()])),
        });
      }
      const stat = forms.get(df.name);
      stat.dancerIds.add(rsvp.dancer_id);
      if (df.level && stat.levels[df.level]) {
        stat.levels[df.level].add(rsvp.dancer_id);
      }
    }
  }

  return [...forms.entries()]
    .map(([name, stat]) => ({
      name,
      total: stat.dancerIds.size,
      levels: DANCE_LEVELS.map((level) => ({
        level,
        count: stat.levels[level]?.size || 0,
      })),
    }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}
